import { QueryClient, QueryFunction, MutationCache } from "@tanstack/react-query";
import { captureError } from "@/lib/error-reporting";

// Custom error class that preserves JSON error details while maintaining Error instance
class ApiError extends Error {
  status: number;
  code?: string;
  requiresReconnect?: boolean;
  data?: any;
  _sentryReported = false;  // set by captureError to prevent double-capture

  constructor(message: string, status: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;

    // Attach JSON error details as properties
    if (data && typeof data === 'object') {
      this.code = data.code;
      this.requiresReconnect = data.requiresReconnect;
      this.data = data;
    }
  }
}

// Network error class for offline/connectivity issues
class NetworkError extends Error {
  isNetworkError: true = true;
  cause?: Error;
  _sentryReported = false;  // set by captureError to prevent double-capture

  constructor(message: string = 'Network error: Unable to connect to server. Please check your internet connection.', cause?: Error) {
    super(message);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export { ApiError, NetworkError };

/**
 * Read the double-submit CSRF token from the `csrf-token` cookie (set by the
 * server on safe requests). Returns undefined if the cookie is absent.
 */
export function getCsrfToken(): string | undefined {
  const row = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrf-token='));
  return row?.slice('csrf-token='.length);
}

/**
 * CSRF headers for a state-changing (POST/PUT/PATCH/DELETE) raw `fetch` that does
 * not go through `apiRequest`. Merge into the request's headers:
 *   fetch(url, { method: 'POST', headers: { ...csrfHeaders(), ... }, ... })
 * Returns an empty object when no token is present (fails closed server-side).
 */
export function csrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  return token ? { 'x-csrf-token': token } : {};
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // Try to parse JSON error responses
    const contentType = res.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      try {
        const json = await res.json();
        // A failed CSRF double-submit (stale cookie/header after a session change)
        // surfaces to users as a readable prompt, not the raw "CSRF token
        // validation failed" text. Centralised here so every apiRequest caller
        // (incl. the email composer) benefits.
        if (res.status === 403 && json.code === 'CSRF_TOKEN_INVALID') {
          throw new ApiError('Your session expired. Refresh the page and try again.', res.status, json);
        }
        // Create ApiError with message and attached JSON details
        const message = json.message || json.error || res.statusText;
        throw new ApiError(message, res.status, json);
      } catch (e) {
        // If it's already an ApiError from above, re-throw it
        if (e instanceof ApiError) throw e;
        
        // If JSON parsing fails, fall back to text
        throw new Error(`${res.status}: ${res.statusText}`);
      }
    }
    
    // For non-JSON responses (e.g. HTML error pages), use friendly status-based messages
    const friendlyMessages: Record<number, string> = {
      400: 'Invalid request. Please check your input and try again.',
      401: 'Your session has expired. Please log in again.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested resource was not found.',
      408: 'The request timed out. Please try again.',
      413: 'The file is too large. Please reduce the file size and try again.',
      429: 'Too many requests. Please wait a moment and try again.',
      500: 'An unexpected server error occurred. Please try again later.',
      502: 'The server is temporarily unavailable. Please try again later.',
      503: 'The service is temporarily unavailable. Please try again later.',
      504: 'The request timed out. Please try again.',
    };
    const message = friendlyMessages[res.status]
      || `Something went wrong (error ${res.status}). Please try again.`;
    throw new ApiError(message, res.status);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
  options?: { signal?: AbortSignal },
): Promise<Response> {
  const isFormData = data instanceof FormData;

  // Build headers
  const headers: Record<string, string> = {};
  if (data && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  // Include CSRF token for state-changing requests
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    headers["x-csrf-token"] = csrfToken;
  }

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
      credentials: "include",
      signal: options?.signal,
    });

    await throwIfResNotOk(res);
    // Success - reset auth error counter
    resetAuthErrors();
    return res;
  } catch (error) {
    // If it's already an ApiError, check for auth error
    if (error instanceof ApiError) {
      if (error.status === 401) {
        handleAuthError();
      }
      captureError(error);
      throw error;
    }

    // Handle network errors (TypeError from fetch when offline/network error)
    if (error instanceof TypeError) {
      const netErr = new NetworkError(undefined, error);
      captureError(netErr);
      throw netErr;
    }

    // Re-throw any other errors
    captureError(error);
    throw error;
  }
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Separate path segments from query parameters
    const pathSegments = [];
    let queryParams = {};
    
    for (const segment of queryKey) {
      if (typeof segment === 'object' && segment !== null) {
        // This is a query parameters object
        queryParams = segment;
      } else {
        // This is a path segment (string or number)
        pathSegments.push(String(segment));
      }
    }
    
    // Build the URL path
    let url = pathSegments.join('/');
    
    // Add query parameters if any
    if (Object.keys(queryParams).length > 0) {
      const searchParams = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        // Treat booleans explicitly so false is not silently dropped
        if (typeof value === 'boolean') {
          searchParams.append(key, value ? 'true' : 'false');
        } else if (value !== null && value !== undefined && value !== '' && value !== 'all') {
          searchParams.append(key, String(value));
        }
      });
      
      const queryString = searchParams.toString();
      if (queryString) {
        url += '?' + queryString;
      }
    }
    
    try {
      const res = await fetch(url, {
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        // Track this as an auth error even when returning null
        handleAuthError();
        return null;
      }

      await throwIfResNotOk(res);
      // Success - reset auth error counter
      resetAuthErrors();
      return await res.json();
    } catch (error) {
      if (error instanceof ApiError) {
        captureError(error);
        throw error;
      }
      if (error instanceof TypeError) {
        const netErr = new NetworkError(undefined, error);
        captureError(netErr);
        throw netErr;
      }
      captureError(error);
      throw error;
    }
  };

// Track 401 errors to detect session expiration  
let consecutiveAuthErrors = 0;
const AUTH_ERROR_THRESHOLD = 2; // Redirect after 2 consecutive 401s
let isRedirecting = false;

export function handleAuthError() {
  if (isRedirecting) return;
  consecutiveAuthErrors++;
  console.log(`[Auth] 401 error count: ${consecutiveAuthErrors}/${AUTH_ERROR_THRESHOLD}`);
  if (consecutiveAuthErrors >= AUTH_ERROR_THRESHOLD) {
    // Session is definitely expired - redirect to login
    console.log('[Auth] Session expired - redirecting to login');
    isRedirecting = true;
    consecutiveAuthErrors = 0;
    window.location.href = '/api/login';
  }
}

export function resetAuthErrors() {
  if (consecutiveAuthErrors > 0) {
    console.log('[Auth] Resetting auth error count on success');
    consecutiveAuthErrors = 0;
  }
}

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error) => {
      // apiRequest already called captureError; skip if already reported
      if ((error as ApiError | NetworkError)._sentryReported) return;
      captureError(error);
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: (failureCount, error) => {
        // Don't retry auth errors
        if (error instanceof ApiError && error.status === 401) {
          handleAuthError();
          return false;
        }
        resetAuthErrors();
        return false;
      },
    },
    mutations: {
      retry: false,
      onError: (error) => {
        if (error instanceof ApiError && error.status === 401) {
          handleAuthError();
        } else {
          resetAuthErrors();
        }
      },
    },
  },
});
